import { addDays } from 'date-fns';
import { format } from 'date-fns-tz';
import isNil from 'lodash-es/isNil';

import { company } from '~/company/Company';
import { usePickupPoint } from '~/hooks/queries/usePickupPoints';
import { useDateLang } from '~/hooks/useDateLang';
import { userStore } from '~/stores';

/**
 * Estimated delivery date range to the customer's selected pickup point (ПВЗ),
 * computed from the point's `eta` (days) returned by the wms-connector. Returns a
 * localized "d MMMM" range string (e.g. "5 – 10 июня"), or `null` when the company
 * is not external-pickup, no point is selected, or the point has no eta.
 *
 * Shared by the PDP delivery badge (ProductPickupEta) and the checkout pickup
 * summary so both show real calendar dates instead of a minute-based range.
 */
export const usePickupEtaRange = (): string | null => {
  const { locale } = useDateLang();
  const { data: point } = usePickupPoint(
    company.isExternalPickupPoints
      ? userStore.deliveryAddress?.pickupPointId
      : null,
  );

  if (!company.isExternalPickupPoints || !point) {
    return null;
  }

  const maxDays = point.eta?.total_eta;
  const minDays = point.eta?.total_eta_min ?? maxDays;

  if (isNil(maxDays) || isNil(minDays)) {
    return null;
  }

  const formatDay = (days: number) =>
    format(addDays(new Date(), days), 'd MMMM', {
      timeZone: company.config.timeZone,
      locale,
    });

  return minDays === maxDays
    ? formatDay(maxDays)
    : `${formatDay(minDays)} – ${formatDay(maxDays)}`;
};
